import React, { useState, useEffect, memo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { societyInfo } from '../data/mock';
import { Card } from './ui/card';
import { Calendar, Users, MapPin, TreePine } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultAboutImage = 'https://images.unsplash.com/photo-1511884642898-4c92249e20b6?w=800&h=600&fit=crop';

const About = memo(() => {
  const { t, language } = useLanguage();
  const [aboutImage, setAboutImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        setAboutImage(response.data?.about_image || defaultAboutImage);
      } catch (error) {
        console.error('Error fetching about image:', error);
        setAboutImage(defaultAboutImage);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const stats = [
    {
      icon: Calendar,
      label: t.about.established,
      value: societyInfo.established,
      suffix: '',
    },
    {
      icon: Users,
      label: t.about.members,
      value: societyInfo.members,
      suffix: '',
    },
    {
      icon: MapPin,
      label: t.about.area,
      value: societyInfo.area.toLocaleString('sl-SI'),
      suffix: ' ha',
    },
  ];

  return (
    <section id="about" className="py-24 bg-stone-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 text-emerald-400">
                <TreePine className="w-5 h-5" />
                <span className="text-sm font-semibold tracking-wider uppercase">
                  {t.about.title}
                </span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-white">
                {language === 'sl' ? societyInfo.name : societyInfo.nameEn}
              </h2>
            </div>

            <p className="text-lg text-stone-400 leading-relaxed">
              {language === 'sl' ? societyInfo.description : societyInfo.descriptionEn}
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4 pt-4">
              {stats.map((stat, index) => (
                <Card
                  key={index}
                  className="bg-stone-800/50 border-stone-700/50 p-6 text-center hover:bg-stone-800 transition-colors duration-300"
                >
                  <stat.icon className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                  <div className="text-2xl sm:text-3xl font-bold text-white">
                    {stat.value}{stat.suffix}
                  </div>
                  <div className="text-sm text-stone-500 mt-1">{stat.label}</div>
                </Card>
              ))}
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              {/* Placeholder */}
              <div 
                className={`absolute inset-0 bg-gradient-to-br from-emerald-900 to-stone-800 transition-opacity duration-500 ${
                  imageLoaded ? 'opacity-0' : 'opacity-100'
                }`}
              />
              {aboutImage && (
                <img
                  src={aboutImage}
                  alt="Lovska družina Rakek - gozdna pokrajina"
                  className={`w-full h-[500px] object-cover transition-opacity duration-500 ${
                    imageLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  loading="lazy"
                  decoding="async"
                  onLoad={() => setImageLoaded(true)}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-transparent" />
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-emerald-600/20 rounded-2xl -z-10" />
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-amber-600/20 rounded-2xl -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
});

About.displayName = 'About';

export default About;
